import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, FileText, Plus, Pencil, Trash2, Check, X, Clock, 
  Send, DollarSign, AlertCircle, Eye, Printer, Download, Mail,
  Package, ChevronRight, Search, MoreVertical, QrCode, CreditCard,
  CheckCircle, XCircle, Copy, Home, Users, Receipt, Calculator, Menu,
  Phone, MapPin, ChevronDown, RefreshCw, Signature, EyeOff, UserPlus, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format, addWeeks, differenceInDays } from "date-fns";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";
import { useCRM } from "@/context/CRMContext";
import FabricEstimator from "@/components/FabricEstimator";
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: "draft", label: "Draft", color: "text-slate-500", bg: "bg-slate-100" },
  { value: "sent", label: "Sent", color: "text-blue-600", bg: "bg-blue-50" },
  { value: "partial", label: "Partial", color: "text-amber-600", bg: "bg-amber-50" },
  { value: "paid", label: "Paid", color: "text-emerald-600", bg: "bg-emerald-50" },
  { value: "overdue", label: "Overdue", color: "text-red-600", bg: "bg-red-50" },
  { value: "cancelled", label: "Cancelled", color: "text-slate-400", bg: "bg-slate-50" }
];

const AdminInvoicePage = () => {
  const navigate = useNavigate();
  const { FABRIC_DATA } = useFabricCatalog();
  const { clients: crmClients, loading: loadingCRM, fetchClients } = useCRM();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("home"); // home, clients, invoices, create, preview
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showActions, setShowActions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOverview, setShowOverview] = useState(true);
  
  // Fabric Estimator Modal
  const [showEstimator, setShowEstimator] = useState(false);
  const [estimatorItemIndex, setEstimatorItemIndex] = useState(null);
  
  // Print/PDF state
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const printableInvoiceRef = useRef(null);
  
  // Fabric & Lining search state
  const [fabricSearches, setFabricSearches] = useState({});
  const [liningSearches, setLiningSearches] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    invoice_number: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    client_address: "",
    items: [{ description: "", quantity: 1, unit_price: 0, total: 0, garment_type: "", fabric_code: "", lining_code: "", button_code: "", from_estimator: false }],
    tax_rate: 8,
    discount: 0,
    deposit_paid: 0,
    status: "draft",
    due_date: "",
    estimated_arrival: "",
    notes: "",
    payment_venmo: "@Richardlaguerre",
    payment_zelle: "9146194784"
  });

  // Button search state
  const [buttonSearches, setButtonSearches] = useState({});

  // Garment types
  const garmentTypes = [
    { value: "2pc-suit", label: "2 Piece Suit", hasLining: true },
    { value: "3pc-suit", label: "3 Piece Suit", hasLining: true },
    { value: "jacket", label: "Jacket / Blazer", hasLining: true },
    { value: "overcoat", label: "Overcoat", hasLining: true },
    { value: "pants", label: "Pants / Trousers", hasLining: true },
    { value: "vest", label: "Vest / Waistcoat", hasLining: true },
    { value: "shirt", label: "Shirt", hasLining: false },
    { value: "tuxedo", label: "Tuxedo", hasLining: true },
    { value: "other", label: "Other", hasLining: false }
  ];

  // Check if garment type has lining
  const hasLining = (garmentType) => {
    const garment = garmentTypes.find(g => g.value === garmentType);
    return garment?.hasLining || false;
  };

  // Combine all fabrics for dropdown (suits + overcoats)
  const allFabrics = useMemo(() => {
    return [
      ...(FABRIC_DATA.suits || []).map(f => ({ ...f, category: 'Suit' })),
      ...(FABRIC_DATA.overcoats || []).map(f => ({ ...f, category: 'Overcoat' }))
    ];
  }, [FABRIC_DATA]);

  // Linings for dropdown
  const allLinings = useMemo(() => {
    return FABRIC_DATA.linings || [];
  }, [FABRIC_DATA]);

  // Filter fabrics based on search
  const getFilteredFabrics = (category, searchTerm) => {
    const fabrics = category === 'shirts' ? FABRIC_DATA.shirts : 
                   category === 'overcoats' ? FABRIC_DATA.overcoats : 
                   FABRIC_DATA.suits;
    if (!searchTerm) return fabrics;
    const search = searchTerm.toLowerCase();
    return fabrics.filter(f => 
      f.rc_code.toLowerCase().includes(search) ||
      f.color?.toLowerCase().includes(search) ||
      f.composition?.toLowerCase().includes(search)
    );
  };

  // Filter linings based on search
  const getFilteredLinings = (searchTerm) => {
    if (!searchTerm) return allLinings;
    const search = searchTerm.toLowerCase();
    return allLinings.filter(l => 
      l.rc_code.toLowerCase().includes(search) ||
      l.color?.toLowerCase().includes(search) ||
      l.composition?.toLowerCase().includes(search)
    );
  };

  // Get buttons based on garment type
  const getButtonCategory = (garmentType) => {
    if (garmentType === 'shirt') return 'shirt';
    if (garmentType === 'overcoat') return 'overcoat';
    return 'suit';
  };

  // Get all buttons for a garment type
  const getButtonsForGarment = (garmentType) => {
    const category = getButtonCategory(garmentType);
    return FABRIC_DATA.buttons?.[category] || [];
  };

  // Filter buttons based on search
  const getFilteredButtons = (garmentType, searchTerm) => {
    const buttons = getButtonsForGarment(garmentType);
    if (!searchTerm) return buttons;
    const search = searchTerm.toLowerCase();
    return buttons.filter(b => 
      b.code.toLowerCase().includes(search) ||
      b.name?.toLowerCase().includes(search) ||
      b.material?.toLowerCase().includes(search) ||
      b.color?.toLowerCase().includes(search)
    );
  };

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
    } else {
      setIsAuthenticated(true);
      fetchInvoices();
    }
  }, [navigate]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await axios.get(`${API}/invoices/next-number`);
      return response.data.next_number;
    } catch (error) {
      return `INV-${new Date().getFullYear()}-001`;
    }
  };

  // Stats calculations
  const stats = useMemo(() => {
    const overdue = invoices.filter(i => i.status === "overdue");
    const unpaid = invoices.filter(i => ["sent", "partial", "overdue"].includes(i.status));
    const unsent = invoices.filter(i => i.status === "draft");
    const paid = invoices.filter(i => i.status === "paid");
    
    return {
      overdueCount: overdue.length,
      overdueAmount: overdue.reduce((sum, i) => sum + i.balance_due, 0),
      unpaidCount: unpaid.length,
      unpaidAmount: unpaid.reduce((sum, i) => sum + i.balance_due, 0),
      unsentCount: unsent.length,
      unsentAmount: unsent.reduce((sum, i) => sum + i.total, 0),
      paidAmount: paid.reduce((sum, i) => sum + i.total, 0)
    };
  }, [invoices]);

  // Clients grouped from invoices
  const clients = useMemo(() => {
    const clientMap = {};
    invoices.forEach(inv => {
      if (!clientMap[inv.client_name]) {
        clientMap[inv.client_name] = {
          name: inv.client_name,
          email: inv.client_email,
          phone: inv.client_phone,
          totalOwed: 0,
          invoiceCount: 0
        };
      }
      if (["sent", "partial", "overdue"].includes(inv.status)) {
        clientMap[inv.client_name].totalOwed += inv.balance_due;
      }
      clientMap[inv.client_name].invoiceCount++;
    });
    return Object.values(clientMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  // Alphabetical index
  const alphabetIndex = useMemo(() => {
    const letters = new Set();
    clients.forEach(c => {
      if (c.name) letters.add(c.name[0].toUpperCase());
    });
    return Array.from(letters).sort();
  }, [clients]);

  const handleNewInvoice = async () => {
    const nextNumber = await fetchNextInvoiceNumber();
    const defaultArrival = format(addWeeks(new Date(), 4), "yyyy-MM-dd");
    const today = format(new Date(), "yyyy-MM-dd");
    setFormData({
      invoice_number: nextNumber,
      client_name: "",
      client_email: "",
      client_phone: "",
      client_address: "",
      items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
      tax_rate: 8,
      discount: 0,
      deposit_paid: 0,
      status: "draft",
      due_date: today,
      estimated_arrival: defaultArrival,
      notes: "Thank you for Shopping with Laguerre!\n\nWe are committed to providing our customers with exceptional service.\n\n1. Turnaround is 4-6 weeks from order finalization.\n2. No returns/exchanges/refunds for custom-designed items.\n3. Complimentary alterations within 60 days of pickup.\n4. Garments left over 60 days may be donated.",
      payment_venmo: "@Richardlaguerre",
      payment_zelle: "9146194784"
    });
    setSelectedInvoice(null);
    setActiveTab("create");
  };

  const calculateTotals = (items, taxRate, discount, depositPaid) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxAmount = (subtotal - discount) * (taxRate / 100);
    const total = subtotal - discount + taxAmount;
    const balanceDue = total - depositPaid;
    return { subtotal, taxAmount, total, balanceDue };
  };

  const handleSubmit = async () => {
    if (!formData.invoice_number || !formData.client_name) {
      toast.error("Please fill in required fields");
      return;
    }
    
    // Validate email if provided
    if (formData.client_email && !isValidEmail(formData.client_email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        items: formData.items.filter(item => item.description)
      };

      if (selectedInvoice) {
        await axios.put(`${API}/invoices/${selectedInvoice.id}`, dataToSend);
        toast.success("Invoice updated!");
      } else {
        await axios.post(`${API}/invoices`, dataToSend);
        toast.success("Invoice created!");
      }
      
      setActiveTab("invoices");
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to save invoice");
    }
  };

  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      invoice_number: invoice.invoice_number,
      client_name: invoice.client_name,
      client_email: invoice.client_email || "",
      client_phone: invoice.client_phone || "",
      client_address: invoice.client_address || "",
      items: invoice.items.length > 0 ? invoice.items : [{ description: "", quantity: 1, unit_price: 0, total: 0 }],
      tax_rate: invoice.tax_rate || 8,
      discount: invoice.discount || 0,
      deposit_paid: invoice.deposit_paid || 0,
      status: invoice.status,
      due_date: invoice.due_date || "",
      estimated_arrival: invoice.estimated_arrival || "",
      notes: invoice.notes || "",
      payment_venmo: invoice.payment_venmo || "@Richardlaguerre",
      payment_zelle: invoice.payment_zelle || "9146194784"
    });
    setActiveTab("create");
    setShowActions(false);
  };

  const handleDelete = async (invoiceId, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm("Delete this invoice?")) return;
    
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      setActiveTab("invoices");
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // Copy invoice state
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyOptions, setCopyOptions] = useState({
    copyClient: true,
    copyItems: true
  });
  const [invoiceToCopy, setInvoiceToCopy] = useState(null);

  const handleCopyInvoice = async (invoice) => {
    setInvoiceToCopy(invoice);
    setCopyOptions({ copyClient: true, copyItems: true });
    setShowCopyDialog(true);
  };

  const executeCopyInvoice = async () => {
    if (!invoiceToCopy) return;
    
    try {
      // Get next invoice number
      const nextNumber = await fetchNextInvoiceNumber();
      
      // Create new invoice data based on copy options
      const newInvoiceData = {
        invoice_number: nextNumber,
        client_name: copyOptions.copyClient ? invoiceToCopy.client_name : "",
        client_email: copyOptions.copyClient ? (invoiceToCopy.client_email || "") : "",
        client_phone: copyOptions.copyClient ? (invoiceToCopy.client_phone || "") : "",
        client_address: copyOptions.copyClient ? (invoiceToCopy.client_address || "") : "",
        items: copyOptions.copyItems 
          ? invoiceToCopy.items.map(item => ({ ...item })) 
          : [{ description: "", quantity: 1, unit_price: 0, total: 0, garment_type: "", fabric_code: "", lining_code: "", button_code: "", from_estimator: false }],
        tax_rate: invoiceToCopy.tax_rate || 8,
        discount: 0,
        deposit_paid: 0,
        status: "draft",
        due_date: "",
        estimated_arrival: invoiceToCopy.estimated_arrival || "",
        notes: "",
        payment_venmo: invoiceToCopy.payment_venmo || "@Richardlaguerre",
        payment_zelle: invoiceToCopy.payment_zelle || "9146194784"
      };
      
      // Set form data and go to create tab
      setFormData(newInvoiceData);
      setSelectedInvoice(null); // Clear selected so it creates new, not updates
      setShowCopyDialog(false);
      setInvoiceToCopy(null);
      setActiveTab("create");
      toast.success(`Invoice copied! New invoice: ${nextNumber}`);
    } catch (error) {
      console.error("Failed to copy invoice:", error);
      toast.error("Failed to copy invoice");
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    try {
      await axios.put(`${API}/invoices/${invoice.id}`, { 
        status: "paid",
        deposit_paid: invoice.total
      });
      toast.success("Marked as paid!");
      fetchInvoices();
      setShowActions(false);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleMarkAsUnpaid = async (invoice) => {
    try {
      await axios.put(`${API}/invoices/${invoice.id}`, { 
        status: "sent",
        deposit_paid: 0
      });
      toast.success("Marked as unpaid!");
      fetchInvoices();
      setShowActions(false);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleMarkAsSent = async (invoice) => {
    try {
      await axios.put(`${API}/invoices/${invoice.id}`, { status: "sent" });
      toast.success("Marked as sent!");
      fetchInvoices();
      setShowActions(false);
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Email validation helper
  const isValidEmail = (email) => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const handleSendInvoiceEmail = async (invoice) => {
    if (!invoice.client_email) {
      toast.error("No email address set for this client");
      return;
    }
    
    if (!isValidEmail(invoice.client_email)) {
      toast.error("Invalid email address. Please update the client email before sending.");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoice.id}/send-email`);
      toast.success(`Invoice sent to ${invoice.client_email}`);
      fetchInvoices(); // Refresh to update status
      setShowActions(false);
    } catch (error) {
      console.error("Email error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to send email";
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, total: 0, garment_type: "", fabric_code: "", lining_code: "", button_code: "", from_estimator: false }]
    });
  };

  // Open Cost Calculator for a specific item
  const openEstimatorForItem = (index) => {
    setEstimatorItemIndex(index);
    setShowEstimator(true);
  };

  // Handle price calculated from Cost Calculator
  const handlePriceCalculated = (calculatedPrice) => {
    if (estimatorItemIndex !== null && calculatedPrice) {
      const newItems = [...formData.items];
      newItems[estimatorItemIndex].unit_price = calculatedPrice;
      newItems[estimatorItemIndex].from_estimator = true;
      // Recalculate total
      newItems[estimatorItemIndex].total = newItems[estimatorItemIndex].quantity * calculatedPrice;
      setFormData({ ...formData, items: newItems });
    }
    setShowEstimator(false);
    setEstimatorItemIndex(null);
  };

  // Clear estimator data from an item
  const clearEstimatorFromItem = (index) => {
    const newItems = [...formData.items];
    newItems[index].from_estimator = false;
    // Remove fabric info from description
    newItems[index].description = newItems[index].description.replace(/ - Fabric: [\d.]+m/, '').replace(/Fabric: [\d.]+m/, '');
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length > 0 ? newItems : [{ description: "", quantity: 1, unit_price: 0, total: 0, garment_type: "", fabric_code: "", lining_code: "" }] });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === "quantity" || field === "unit_price" ? parseFloat(value) || 0 : value;
    newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    setFormData({ ...formData, items: newItems });
  };

  // Helper to update garment type and auto-fill description
  const handleGarmentSelect = (index, garmentType) => {
    const garment = garmentTypes.find(g => g.value === garmentType);
    const newItems = [...formData.items];
    newItems[index].garment_type = garmentType;
    
    // Clear lining if garment doesn't support it
    if (!garment?.hasLining) {
      newItems[index].lining_code = "";
    }
    
    // Auto-update description with garment type
    if (garment) {
      const currentDesc = newItems[index].description;
      // If description is empty or only has fabric/lining codes, set garment as base
      if (!currentDesc || currentDesc.startsWith('Fabric #') || currentDesc.startsWith('Lining #')) {
        newItems[index].description = garment.label;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  // Helper to update fabric selection and auto-fill description
  const handleFabricSelect = (index, fabricCode) => {
    const fabric = allFabrics.find(f => f.rc_code === fabricCode);
    const newItems = [...formData.items];
    newItems[index].fabric_code = fabricCode;
    if (fabric) {
      // Auto-update description with fabric info
      const currentDesc = newItems[index].description;
      const fabricInfo = `Fabric #${fabricCode}`;
      if (!currentDesc.includes('Fabric #')) {
        newItems[index].description = currentDesc ? `${currentDesc} - ${fabricInfo}` : fabricInfo;
      } else {
        newItems[index].description = currentDesc.replace(/Fabric #\S+/, fabricInfo);
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  // Helper to update lining selection
  const handleLiningSelect = (index, liningCode) => {
    const newItems = [...formData.items];
    newItems[index].lining_code = liningCode;
    if (liningCode && liningCode !== "none") {
      // Auto-update description with lining info
      const currentDesc = newItems[index].description;
      const liningInfo = `Lining #${liningCode}`;
      if (!currentDesc.includes('Lining #')) {
        newItems[index].description = currentDesc ? `${currentDesc} - ${liningInfo}` : liningInfo;
      } else {
        newItems[index].description = currentDesc.replace(/Lining #\S+/, liningInfo);
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  // Handle selecting a client from CRM
  const handleSelectCRMClient = (clientEmail) => {
    if (clientEmail === "new") {
      // Clear all fields for new client
      setFormData({
        ...formData,
        client_name: "",
        client_email: "",
        client_phone: "",
        client_address: ""
      });
      return;
    }
    
    const client = crmClients.find(c => c.email === clientEmail);
    if (client) {
      setFormData({
        ...formData,
        client_name: client.name || "",
        client_email: client.email || "",
        client_phone: client.phone || "",
        client_address: client.address || ""
      });
    }
  };

  // Handle Print Invoice
  const handlePrintInvoice = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      const printContent = printableInvoiceRef.current;
      if (printContent) {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Invoice ${selectedInvoice?.invoice_number || 'Preview'}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: Arial, sans-serif; padding: 20px; }
              .invoice-container { max-width: 600px; margin: 0 auto; }
              @media print {
                body { padding: 0; }
                @page { margin: 0.5in; }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
      setShowPrintPreview(false);
    }, 100);
  };

  // Handle Export as PDF
  const handleExportPdf = async () => {
    if (!selectedInvoice) {
      toast.error("No invoice selected");
      return;
    }

    setIsExportingPdf(true);
    setShowPrintPreview(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const element = printableInvoiceRef.current;
      if (!element) {
        throw new Error("Invoice element not found");
      }

      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `Invoice-${selectedInvoice.invoice_number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait' 
        }
      };

      await html2pdf().set(opt).from(element).save();
      toast.success(`PDF exported: Invoice-${selectedInvoice.invoice_number}.pdf`);
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setIsExportingPdf(false);
      setShowPrintPreview(false);
    }
  };

  const getStatusConfig = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];

  const getOverdueDays = (dueDate) => {
    if (!dueDate) return 0;
    const days = differenceInDays(new Date(), new Date(dueDate));
    return days > 0 ? days : 0;
  };

  const totals = calculateTotals(formData.items, formData.tax_rate, formData.discount, formData.deposit_paid);

  const filteredInvoices = invoices.filter(inv => 
    searchQuery === "" || 
    inv.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAuthenticated) return null;

  // Invoice Template Component (Invoice2Go Style)
  const InvoiceTemplate = ({ invoice, isForm = false }) => {
    const data = isForm ? formData : invoice;
    const calc = isForm ? totals : { 
      subtotal: invoice?.subtotal || 0, 
      taxAmount: invoice?.tax_amount || 0, 
      total: invoice?.total || 0, 
      balanceDue: invoice?.balance_due || 0 
    };

    return (
      <div className="bg-white text-slate-800 p-6 max-w-xl mx-auto text-sm">
        {/* Header with Logo */}
        <div className="text-center mb-4 pb-4 border-b-2 border-blue-900">
          <img 
            src="https://customer-assets.emergentagent.com/job_laguerre-tailor/artifacts/nrobby08_handwritten%20signature%20logo%20V2%20black.png"
            alt="Laguerre"
            className="h-48 mx-auto object-contain -mb-2"
          />
          <p className="text-xs text-slate-500 -mt-4">Custom Tailoring</p>
        </div>

        {/* Company Info & Invoice Title */}
        <div className="flex justify-between mb-6">
          <div className="text-xs text-slate-600">
            <p>55 W Railroad Ave Building 24</p>
            <p>Garnerville, NY 10923</p>
            <p>United States</p>
            <p className="mt-1">hello@laguerre.co</p>
            <p className="text-primary font-medium">www.laguerre.co</p>
          </div>
          <div className="text-right">
            <h2 className="text-2xl font-bold text-slate-800">Invoice</h2>
          </div>
        </div>

        {/* Invoice Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6 text-xs">
          <div className="space-y-1">
            <div className="flex"><span className="text-slate-500 w-20">Invoice No:</span><span className="font-medium">{data.invoice_number || "---"}</span></div>
            <div className="flex"><span className="text-slate-500 w-20">Date:</span><span>{format(new Date(), "MM/dd/yyyy")}</span></div>
            <div className="flex"><span className="text-slate-500 w-20">Terms:</span><span>NET 0</span></div>
            <div className="flex"><span className="text-slate-500 w-20">Due Date:</span><span>{data.due_date ? format(new Date(data.due_date), "MM/dd/yyyy") : "---"}</span></div>
          </div>
          <div>
            <p className="text-slate-500 mb-1">Bill To:</p>
            <p className="font-medium">{data.client_name || "Client Name"}</p>
            {data.client_phone && <p>{data.client_phone}</p>}
            {data.client_email && <p className="text-slate-500">{data.client_email}</p>}
          </div>
        </div>

        {/* Suit Arrival Badge */}
        {data.estimated_arrival && (
          <div className="bg-blue-50 border border-blue-200 p-2 mb-4 text-center">
            <span className="text-xs text-blue-600 font-medium">
              <Package size={12} className="inline mr-1" />
              Estimated Suit Arrival: {format(new Date(data.estimated_arrival), "MMM dd, yyyy")}
            </span>
          </div>
        )}

        {/* Items Table */}
        <table className="w-full mb-4 text-xs">
          <thead>
            <tr className="border-b-2 border-slate-200">
              <th className="text-left py-2 font-medium text-slate-600">Description</th>
              <th className="text-center py-2 font-medium text-slate-600 w-16">Quantity</th>
              <th className="text-right py-2 font-medium text-slate-600 w-20">Rate</th>
              <th className="text-right py-2 font-medium text-slate-600 w-20">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).filter(item => item.description).map((item, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="py-2 whitespace-pre-line">{item.description}</td>
                <td className="py-2 text-center">{item.quantity}</td>
                <td className="py-2 text-right">${item.unit_price.toFixed(2)}</td>
                <td className="py-2 text-right">${(item.quantity * item.unit_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-4">
          <div className="w-48 text-xs space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>${calc.subtotal.toFixed(2)}</span></div>
            {(isForm ? formData.discount : invoice?.discount) > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>-${(isForm ? formData.discount : invoice?.discount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>TAX {isForm ? formData.tax_rate : invoice?.tax_rate}%</span>
              <span>${calc.taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold border-t border-slate-200 pt-1">
              <span>Total</span>
              <span>${calc.total.toFixed(2)}</span>
            </div>
            {(isForm ? formData.deposit_paid : invoice?.deposit_paid) > 0 && (
              <div className="flex justify-between text-slate-500">
                <span>Paid</span>
                <span>${(isForm ? formData.deposit_paid : invoice?.deposit_paid).toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Balance Due Box */}
        <div className="bg-blue-50 border border-blue-200 p-3 mb-4 flex justify-between items-center">
          <span className="font-bold text-blue-900">Balance Due</span>
          <span className="text-xl font-bold text-blue-900">${calc.balanceDue.toFixed(2)}</span>
        </div>

        {/* Payment Instructions */}
        <div className="mb-4">
          <p className="font-medium text-slate-700 mb-2">Payment Instructions</p>
          <div className="text-xs text-slate-600 space-y-1">
            <p>Venmo {data.payment_venmo || "@Richardlaguerre"}</p>
            <p>Zelle {data.payment_zelle || "9146194784"}</p>
          </div>
        </div>

        {/* Comments */}
        {data.notes && (
          <div className="border-t border-slate-200 pt-4">
            <p className="font-medium text-slate-700 mb-2">Comments</p>
            <p className="text-xs text-slate-600 whitespace-pre-line">{data.notes}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-16">
      {/* Header */}
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-16 z-40">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/admin")} className="text-blue-400 font-medium">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold text-white tracking-wider">INVOICE</h1>
          <button onClick={handleNewInvoice} className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg">
            <Plus size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence mode="wait">
        {/* HOME TAB - Overview Dashboard */}
        {activeTab === "home" && (
          <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto">
            {/* Overview Section */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Overview</h2>
                <button onClick={() => setShowOverview(!showOverview)} className="text-blue-400 text-sm font-medium">
                  {showOverview ? "Hide" : "Show"}
                </button>
              </div>

              {showOverview && (
                <div className="space-y-3">
                  {/* Overdue Invoices */}
                  <div className="bg-[#111c2e] border border-red-500/30 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-[#162033]" onClick={() => setActiveTab("invoices")}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                        <AlertCircle size={20} className="text-red-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Overdue invoices ({stats.overdueCount})</p>
                        <p className="text-xl font-bold text-red-400">${stats.overdueAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500" />
                  </div>

                  {/* Unpaid Invoices */}
                  <div className="bg-[#111c2e] border border-amber-500/30 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-[#162033]" onClick={() => setActiveTab("invoices")}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                        <Clock size={20} className="text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Unpaid invoices ({stats.unpaidCount})</p>
                        <p className="text-xl font-bold text-amber-400">${stats.unpaidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500" />
                  </div>

                  {/* Unsent Invoices */}
                  <div className="bg-[#111c2e] border border-white/10 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-[#162033]" onClick={() => setActiveTab("invoices")}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-500/20 rounded-full flex items-center justify-center">
                        <FileText size={20} className="text-slate-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Unsent invoices ({stats.unsentCount})</p>
                        <p className="text-xl font-bold text-white">${stats.unsentAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500" />
                  </div>

                  {/* Tax Year Sales */}
                  <div className="bg-[#111c2e] border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                        <DollarSign size={20} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-400">Tax year sales {new Date().getFullYear()}</p>
                        <p className="text-xl font-bold text-emerald-400">${stats.paidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* CLIENTS TAB */}
        {activeTab === "clients" && (
          <motion.div key="clients" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto">
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input placeholder="Search clients" className="pl-10 bg-[#111c2e] border-white/10 rounded-xl text-white placeholder:text-slate-500" />
              </div>
            </div>

            {/* Clients List with Alphabet */}
            <div className="relative">
              {/* Alphabet Index */}
              <div className="fixed right-1 top-1/2 -translate-y-1/2 flex flex-col text-[10px] text-blue-400 font-medium z-10">
                {alphabetIndex.map(letter => (
                  <button key={letter} className="py-0.5 px-1 hover:bg-white/10">{letter}</button>
                ))}
              </div>

              {/* Client List */}
              <div className="divide-y divide-white/10">
                {clients.map((client, idx) => {
                  const showLetter = idx === 0 || client.name[0].toUpperCase() !== clients[idx-1]?.name[0]?.toUpperCase();
                  return (
                    <div key={client.name}>
                      {showLetter && (
                        <div className="bg-[#111c2e] px-4 py-1 text-xs font-bold text-slate-400">
                          {client.name[0].toUpperCase()}
                        </div>
                      )}
                      <div className="px-4 py-3 flex items-center justify-between hover:bg-[#111c2e]">
                        <div>
                          <p className="font-medium text-white">{client.name}</p>
                          {client.email && <p className="text-xs text-slate-400">{client.email}</p>}
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-white">${client.totalOwed.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                          {client.totalOwed > 0 && (
                            <p className="text-xs text-blue-400 font-medium">Collect payment</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* INVOICES TAB */}
        {activeTab === "invoices" && (
          <motion.div key="invoices" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto">
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input 
                  placeholder="Search invoices" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#111c2e] border-white/10 rounded-xl text-white placeholder:text-slate-500" 
                />
              </div>
            </div>

            {/* Invoice List */}
            <div className="divide-y divide-white/10">
              {filteredInvoices.map(invoice => {
                const statusConfig = getStatusConfig(invoice.status);
                const overdueDays = getOverdueDays(invoice.due_date);
                return (
                  <div 
                    key={invoice.id} 
                    className="px-4 py-3 flex items-center justify-between hover:bg-[#111c2e] group"
                  >
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => { setSelectedInvoice(invoice); setActiveTab("preview"); }}
                    >
                      <div className={`w-1 h-12 rounded-full ${
                        invoice.status === "paid" ? "bg-emerald-500" :
                        invoice.status === "overdue" ? "bg-red-500" :
                        invoice.status === "sent" ? "bg-blue-500" :
                        "bg-slate-500"
                      }`} />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">Invoice {invoice.invoice_number.split('-').pop()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">{invoice.client_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="text-right cursor-pointer"
                        onClick={() => { setSelectedInvoice(invoice); setActiveTab("preview"); }}
                      >
                        <p className="font-bold text-white">${invoice.total.toFixed(2)}</p>
                        {invoice.status === "overdue" && overdueDays > 0 && (
                          <p className="text-xs text-red-400">Overdue {overdueDays} days</p>
                        )}
                      </div>
                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete Invoice ${invoice.invoice_number}?\n\nClient: ${invoice.client_name}\nAmount: $${invoice.total.toFixed(2)}\n\nThis action cannot be undone.`)) {
                            handleDelete(invoice.id, true);
                          }
                        }}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/20 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete Invoice"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* PREVIEW TAB */}
        {activeTab === "preview" && selectedInvoice && (
          <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {/* Preview Header */}
            <div className="bg-[#111c2e] border-b border-white/10 px-4 py-2 flex items-center justify-between sticky top-28 z-30">
              <button onClick={() => setActiveTab("invoices")} className="text-blue-400 font-medium">Close</button>
              <span className="font-medium text-white">Preview</span>
              <button onClick={() => setShowActions(true)}>
                <MoreVertical size={20} className="text-slate-400" />
              </button>
            </div>

            {/* Invoice Preview */}
            <div className="bg-[#0a1628] p-4">
              <InvoiceTemplate invoice={selectedInvoice} />
            </div>

            {/* Bottom Actions */}
            <div className="bg-[#111c2e] border-t border-white/10 p-4 space-y-3">
              {/* Total & Status */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">${selectedInvoice.total.toFixed(2)}</p>
                  {selectedInvoice.status === "overdue" && (
                    <p className="text-sm text-red-400 font-medium">Overdue {getOverdueDays(selectedInvoice.due_date)} days</p>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusConfig(selectedInvoice.status).bg} ${getStatusConfig(selectedInvoice.status).color}`}>
                  {getStatusConfig(selectedInvoice.status).label.toUpperCase()}
                </span>
              </div>

              {/* Send Button */}
              {selectedInvoice.status === "draft" && (
                <div className="space-y-1">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
                    onClick={() => handleSendInvoiceEmail(selectedInvoice)}
                    disabled={sendingEmail || !selectedInvoice.client_email}
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw size={20} className="mr-2 animate-spin" /> Sending to {selectedInvoice.client_email}...
                      </>
                    ) : selectedInvoice.client_email ? (
                      <>
                        <Send size={20} className="mr-2" /> Send to {selectedInvoice.client_email}
                      </>
                    ) : (
                      <>
                        <Send size={20} className="mr-2" /> Send Invoice
                      </>
                    )}
                  </Button>
                  {!selectedInvoice.client_email && (
                    <p className="text-xs text-center text-red-400">
                      ⚠️ Add client email address to send invoice
                    </p>
                  )}
                </div>
              )}
              
              {/* Resend Email Button - for already sent invoices */}
              {selectedInvoice.status !== "draft" && selectedInvoice.client_email && (
                <div className="space-y-1">
                  <Button 
                    variant="outline"
                    className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/20 py-4"
                    onClick={() => handleSendInvoiceEmail(selectedInvoice)}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? (
                      <>
                        <RefreshCw size={18} className="mr-2 animate-spin" /> Sending to {selectedInvoice.client_email}...
                      </>
                    ) : (
                      <>
                        <Mail size={18} className="mr-2" /> Send to {selectedInvoice.client_email}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Payment Section */}
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs font-medium text-slate-400 mb-2">PAYMENT</p>
                <div className="space-y-2">
                  <button 
                    className="w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                    onClick={() => handleMarkAsPaid(selectedInvoice)}
                  >
                    <CheckCircle size={20} className="text-emerald-400" />
                    <span className="font-medium">Mark as paid</span>
                  </button>
                  <button className="w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white">
                    <QrCode size={20} className="text-blue-400" />
                    <span className="font-medium">Show QR Code</span>
                    <span className="ml-auto text-xs bg-blue-500/30 text-blue-400 px-2 py-0.5 rounded">NEW</span>
                  </button>
                  <button className="w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white">
                    <CreditCard size={20} className="text-slate-400" />
                    <span className="font-medium">Record payment</span>
                  </button>
                </div>
              </div>
              
              {/* Print & Export Section */}
              <div className="pt-3 border-t border-white/10">
                <p className="text-xs font-medium text-slate-400 mb-2">EXPORT</p>
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    className="flex items-center justify-center gap-2 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                    onClick={handlePrintInvoice}
                  >
                    <Printer size={18} className="text-blue-400" />
                    <span className="font-medium text-sm">Print</span>
                  </button>
                  <button 
                    className="flex items-center justify-center gap-2 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                    onClick={handleExportPdf}
                    disabled={isExportingPdf}
                  >
                    {isExportingPdf ? (
                      <Loader2 size={18} className="text-green-400 animate-spin" />
                    ) : (
                      <Download size={18} className="text-green-400" />
                    )}
                    <span className="font-medium text-sm">{isExportingPdf ? "Exporting..." : "PDF"}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Actions Modal */}
            {showActions && (
              <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowActions(false)}>
                <motion.div 
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  className="absolute bottom-0 left-0 right-0 bg-[#111c2e] rounded-t-2xl p-4 max-h-[80vh] overflow-y-auto"
                  onClick={e => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-4">
                    <button onClick={() => setShowActions(false)} className="text-blue-400 font-semibold">Done</button>
                    <span className="font-semibold text-white">Invoice {selectedInvoice.invoice_number.split('-').pop()}</span>
                    <button onClick={() => handleEdit(selectedInvoice)} className="text-blue-400 font-semibold">Edit</button>
                  </div>

                  {/* Back to Preview Button */}
                  <button 
                    className="w-full flex items-center gap-3 p-4 bg-[#0a1628] border-2 border-white/20 rounded-xl mb-4 text-white"
                    onClick={() => setShowActions(false)}
                  >
                    <ArrowLeft size={20} className="text-white" />
                    <span className="font-medium">Back to Invoice Preview</span>
                  </button>

                  {/* Payment Section */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-400 mb-2 px-2 uppercase tracking-wide">PAYMENT</p>
                    <div className="bg-[#0a1628] rounded-xl divide-y divide-white/10">
                      {selectedInvoice.status !== "paid" ? (
                        <button className="w-full flex items-center gap-3 p-4 text-white" onClick={() => handleMarkAsPaid(selectedInvoice)}>
                          <CheckCircle size={20} className="text-emerald-400" />
                          <span className="font-medium">Mark as paid</span>
                        </button>
                      ) : (
                        <button className="w-full flex items-center gap-3 p-4 text-white" onClick={() => handleMarkAsUnpaid(selectedInvoice)}>
                          <XCircle size={20} className="text-orange-400" />
                          <span className="font-medium">Mark as unpaid</span>
                        </button>
                      )}
                      <button className="w-full flex items-center gap-3 p-4 text-white">
                        <QrCode size={20} className="text-white" />
                        <span className="font-medium">Show QR Code</span>
                        <span className="ml-auto text-xs bg-blue-500/30 text-blue-400 px-2 py-0.5 rounded font-medium">NEW</span>
                      </button>
                      <button className="w-full flex items-center gap-3 p-4 text-white">
                        <CreditCard size={20} className="text-white" />
                        <span className="font-medium">Record payment</span>
                      </button>
                    </div>
                  </div>

                  {/* More Section */}
                  <div className="mb-4">
                    <p className="text-xs font-bold text-slate-400 mb-2 px-2 uppercase tracking-wide">MORE</p>
                    <div className="bg-[#0a1628] rounded-xl divide-y divide-white/10">
                      <button 
                        className="w-full flex items-center gap-3 p-4"
                        onClick={() => handleSendInvoiceEmail(selectedInvoice)}
                        disabled={sendingEmail || !selectedInvoice.client_email}
                      >
                        <Mail size={20} className={selectedInvoice.client_email ? "text-white" : "text-slate-500"} />
                        <span className={`font-medium ${!selectedInvoice.client_email ? "text-slate-500" : "text-white"}`}>
                          {sendingEmail ? "Sending..." : "Email Invoice"}
                        </span>
                        {!selectedInvoice.client_email && (
                          <span className="ml-auto text-xs text-slate-500">No email</span>
                        )}
                      </button>
                      <button className="w-full flex items-center justify-between p-4 text-white">
                        <div className="flex items-center gap-3">
                          <RefreshCw size={20} className="text-white" />
                          <span className="font-medium">Recurring invoice</span>
                        </div>
                        <span className="text-slate-400">Off</span>
                      </button>
                      <button className="w-full flex items-center gap-3 p-4 text-white" onClick={handlePrintInvoice}>
                        <Printer size={20} className="text-white" />
                        <span className="font-medium">Print</span>
                      </button>
                      <button 
                        className="w-full flex items-center gap-3 p-4 text-white"
                        onClick={() => handleCopyInvoice(selectedInvoice)}
                      >
                        <Copy size={20} className="text-white" />
                        <span className="font-medium">Copy Invoice</span>
                      </button>
                      <button 
                        className="w-full flex items-center gap-3 p-4 text-white"
                        onClick={handleExportPdf}
                        disabled={isExportingPdf}
                      >
                        {isExportingPdf ? (
                          <Loader2 size={20} className="text-white animate-spin" />
                        ) : (
                          <Download size={20} className="text-white" />
                        )}
                        <span className="font-medium">{isExportingPdf ? "Exporting..." : "Export as PDF"}</span>
                      </button>
                      <button className="w-full flex items-center gap-3 p-4 text-white">
                        <Signature size={20} className="text-white" />
                        <span className="font-medium">Sign</span>
                      </button>
                    </div>
                  </div>

                  {/* Mark as sent */}
                  <button 
                    className="w-full flex items-center gap-3 p-4 bg-[#0a1628] rounded-xl mb-3 text-white"
                    onClick={() => handleMarkAsSent(selectedInvoice)}
                  >
                    <Send size={20} className="text-white" />
                    <span className="font-medium">Mark as sent</span>
                  </button>

                  {/* Delete */}
                  <button 
                    className="w-full flex items-center gap-3 p-4 bg-red-500/20 rounded-xl text-red-400"
                    onClick={() => handleDelete(selectedInvoice.id)}
                  >
                    <Trash2 size={20} />
                    <span>Delete</span>
                  </button>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}

        {/* CREATE/EDIT TAB */}
        {activeTab === "create" && (
          <motion.div key="create" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-lg mx-auto pb-24">
            {/* Form Header */}
            <div className="bg-[#111c2e] border-b border-white/10 px-4 py-3 flex items-center justify-between sticky top-28 z-30">
              <button onClick={() => setActiveTab("invoices")} className="text-blue-400 font-medium">Cancel</button>
              <span className="font-medium text-white">{selectedInvoice ? "Edit Invoice" : "New Invoice"}</span>
              <button onClick={handleSubmit} className="text-blue-400 font-medium">Save</button>
            </div>

            <div className="p-4 space-y-4">
              {/* Invoice Number & Status */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400">Invoice #</label>
                    <Input value={formData.invoice_number} onChange={(e) => setFormData({...formData, invoice_number: e.target.value})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Status</label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger className="mt-1 bg-[#0a1628] border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Due Date</label>
                    <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-blue-400 flex items-center gap-1"><Package size={12} /> Suit Arrival</label>
                    <Input type="date" value={formData.estimated_arrival} onChange={(e) => setFormData({...formData, estimated_arrival: e.target.value})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                </div>
              </div>

              {/* Client Info */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-white">Bill To</h3>
                  <div className="flex items-center gap-2">
                    <Select onValueChange={handleSelectCRMClient}>
                      <SelectTrigger className="w-[180px] bg-[#0a1628] border-white/10 text-white text-xs h-8">
                        <UserPlus size={14} className="mr-1" />
                        <SelectValue placeholder="Select from CRM" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="new">
                          <span className="text-blue-400">+ New Client</span>
                        </SelectItem>
                        {crmClients.map(client => (
                          <SelectItem key={client.email} value={client.email}>
                            <div className="flex flex-col">
                              <span>{client.name}</span>
                              <span className="text-xs text-slate-400">{client.email}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-3">
                  <Input placeholder="Client name *" value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500" />
                  <Input placeholder="Phone" value={formData.client_phone} onChange={(e) => setFormData({...formData, client_phone: e.target.value})} className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500" />
                  <Input placeholder="Email" value={formData.client_email} onChange={(e) => setFormData({...formData, client_email: e.target.value})} className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500" />
                </div>
              </div>

              {/* Line Items */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <h3 className="font-medium text-white mb-3">Items</h3>
                {formData.items.map((item, idx) => (
                  <div key={idx} className="mb-4 pb-4 border-b border-white/10">
                    {/* Garment Type Selector */}
                    <div className="mb-3">
                      <label className="text-xs text-slate-400 mb-1 block">Garment Type</label>
                      <Select 
                        value={item.garment_type || "none"} 
                        onValueChange={(v) => handleGarmentSelect(idx, v === "none" ? "" : v)}
                      >
                        <SelectTrigger className="bg-[#0a1628] border-white/10 text-white">
                          <SelectValue placeholder="Select garment type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select garment type...</SelectItem>
                          {garmentTypes.map(g => (
                            <SelectItem key={g.value} value={g.value}>
                              {g.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description */}
                    <Input placeholder="Description (auto-fills with selections)" value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="mb-3 bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500" />
                    
                    {/* Fabric & Lining Selectors */}
                    <div className={`grid gap-2 mb-3 ${hasLining(item.garment_type) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">Fabric</label>
                        <Select 
                          value={item.fabric_code || "none"} 
                          onValueChange={(v) => { handleFabricSelect(idx, v === "none" ? "" : v); setFabricSearches(prev => ({...prev, [idx]: ""})); }}
                        >
                          <SelectTrigger className="bg-[#0a1628] border-white/10 text-white text-xs h-9">
                            <SelectValue placeholder="No fabric selected" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {/* Search Input */}
                            <div className="px-2 py-2 border-b border-white/10 sticky top-0 bg-[#1a2744] z-10">
                              <div className="relative">
                                <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                                <input
                                  type="text"
                                  placeholder="Search fabric code..."
                                  value={fabricSearches[idx] || ""}
                                  onChange={(e) => setFabricSearches(prev => ({...prev, [idx]: e.target.value}))}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full bg-[#0a1628] border border-white/10 rounded px-7 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                                />
                                {fabricSearches[idx] && (
                                  <button onClick={(e) => { e.stopPropagation(); setFabricSearches(prev => ({...prev, [idx]: ""})); }} className="absolute right-2 top-2 text-slate-400 hover:text-white">
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <SelectItem value="none">No fabric selected</SelectItem>
                            
                            {/* For Shirts: Show Shirts + Suits fabrics */}
                            {item.garment_type === "shirt" && (
                              <>
                                {getFilteredFabrics('shirts', fabricSearches[idx]).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-slate-400 font-semibold bg-slate-800">Shirts ({getFilteredFabrics('shirts', fabricSearches[idx]).length})</div>
                                    {getFilteredFabrics('shirts', fabricSearches[idx]).map(f => (
                                      <SelectItem key={`shirt-${f.rc_code}`} value={f.rc_code}>
                                        <span className="font-mono text-blue-400">{f.rc_code}</span>
                                        <span className="text-slate-400 ml-2 text-xs">{f.color}</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {getFilteredFabrics('suits', fabricSearches[idx]).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-slate-400 font-semibold bg-slate-800">Suits ({getFilteredFabrics('suits', fabricSearches[idx]).length})</div>
                                    {getFilteredFabrics('suits', fabricSearches[idx]).map(f => (
                                      <SelectItem key={`suit-${f.rc_code}`} value={f.rc_code}>
                                        <span className="font-mono text-blue-400">{f.rc_code}</span>
                                        <span className="text-slate-400 ml-2 text-xs">{f.color}</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                            
                            {/* For Overcoats: Show only Overcoat fabrics */}
                            {item.garment_type === "overcoat" && (
                              <>
                                {getFilteredFabrics('overcoats', fabricSearches[idx]).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-slate-400 font-semibold bg-slate-800">Overcoats ({getFilteredFabrics('overcoats', fabricSearches[idx]).length})</div>
                                    {getFilteredFabrics('overcoats', fabricSearches[idx]).map(f => (
                                      <SelectItem key={`coat-${f.rc_code}`} value={f.rc_code}>
                                        <span className="font-mono text-blue-400">{f.rc_code}</span>
                                        <span className="text-slate-400 ml-2 text-xs">{f.color}</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                            
                            {/* For Suits, Jackets, Pants, Vests, Tuxedo, Other: Show Suits + Overcoats */}
                            {item.garment_type !== "shirt" && item.garment_type !== "overcoat" && (
                              <>
                                {getFilteredFabrics('suits', fabricSearches[idx]).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-slate-400 font-semibold bg-slate-800">Suits ({getFilteredFabrics('suits', fabricSearches[idx]).length})</div>
                                    {getFilteredFabrics('suits', fabricSearches[idx]).map(f => (
                                      <SelectItem key={`suit-${f.rc_code}`} value={f.rc_code}>
                                        <span className="font-mono text-blue-400">{f.rc_code}</span>
                                        <span className="text-slate-400 ml-2 text-xs">{f.color}</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {getFilteredFabrics('overcoats', fabricSearches[idx]).length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-slate-400 font-semibold bg-slate-800">Overcoats ({getFilteredFabrics('overcoats', fabricSearches[idx]).length})</div>
                                    {getFilteredFabrics('overcoats', fabricSearches[idx]).map(f => (
                                      <SelectItem key={`coat-${f.rc_code}`} value={f.rc_code}>
                                        <span className="font-mono text-blue-400">{f.rc_code}</span>
                                        <span className="text-slate-400 ml-2 text-xs">{f.color}</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Lining - Only show for garments that have lining */}
                      {hasLining(item.garment_type) && (
                        <div>
                          <label className="text-xs text-cyan-400 mb-1 block">Lining</label>
                          <Select 
                            value={item.lining_code || "none"} 
                            onValueChange={(v) => { handleLiningSelect(idx, v === "none" ? "" : v); setLiningSearches(prev => ({...prev, [idx]: ""})); }}
                          >
                            <SelectTrigger className="bg-[#0a1628] border-white/10 text-white text-xs h-9">
                              <SelectValue placeholder="No lining selected" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                              {/* Search Input */}
                              <div className="px-2 py-2 border-b border-white/10 sticky top-0 bg-[#1a2744] z-10">
                                <div className="relative">
                                  <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                                  <input
                                    type="text"
                                    placeholder="Search lining code..."
                                    value={liningSearches[idx] || ""}
                                    onChange={(e) => setLiningSearches(prev => ({...prev, [idx]: e.target.value}))}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full bg-[#0a1628] border border-white/10 rounded px-7 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
                                  />
                                  {liningSearches[idx] && (
                                    <button onClick={(e) => { e.stopPropagation(); setLiningSearches(prev => ({...prev, [idx]: ""})); }} className="absolute right-2 top-2 text-slate-400 hover:text-white">
                                      <X size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <SelectItem value="none">No lining selected</SelectItem>
                              {getFilteredLinings(liningSearches[idx]).map(l => (
                                <SelectItem key={`lining-${l.rc_code}`} value={l.rc_code}>
                                  <span className="font-mono text-cyan-400">{l.rc_code}</span>
                                  <span className="text-slate-400 ml-2 text-xs">{l.color}</span>
                                  {l.is_new && <span className="text-green-400 ml-1 text-xs">NEW</span>}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Button Selection */}
                    {item.garment_type && item.garment_type !== 'other' && (
                      <div className="mt-2">
                        <label className="text-xs text-amber-400 mb-1 block">Button</label>
                        <Select 
                          value={item.button_code || "none"} 
                          onValueChange={(v) => { 
                            const newItems = [...formData.items];
                            newItems[idx].button_code = v === "none" ? "" : v;
                            setFormData({ ...formData, items: newItems }); 
                            setButtonSearches(prev => ({...prev, [idx]: ""})); 
                          }}
                        >
                          <SelectTrigger className="bg-[#0a1628] border-white/10 text-white text-xs h-9">
                            <SelectValue placeholder="No button selected" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {/* Search Input */}
                            <div className="px-2 py-2 border-b border-white/10 sticky top-0 bg-[#1a2744] z-10">
                              <div className="relative">
                                <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                                <input
                                  type="text"
                                  placeholder="Search button code..."
                                  value={buttonSearches[idx] || ""}
                                  onChange={(e) => setButtonSearches(prev => ({...prev, [idx]: e.target.value}))}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full bg-[#0a1628] border border-white/10 rounded px-7 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
                                />
                                {buttonSearches[idx] && (
                                  <button onClick={(e) => { e.stopPropagation(); setButtonSearches(prev => ({...prev, [idx]: ""})); }} className="absolute right-2 top-2 text-slate-400 hover:text-white">
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                            <SelectItem value="none">No button selected</SelectItem>
                            {getFilteredButtons(item.garment_type, buttonSearches[idx]).map(b => (
                              <SelectItem key={`button-${b.code}`} value={b.code}>
                                <span className="font-mono text-amber-400">{b.code}</span>
                                <span className="text-slate-400 ml-2 text-xs">{b.name}</span>
                                <span className="text-slate-500 ml-1 text-xs">({b.material})</span>
                                <span className="text-amber-300 ml-1 text-xs">${b.price}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Quantity, Price, Total with Estimator */}
                    <div className="flex gap-2 items-center">
                      <Input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="w-16 text-center bg-[#0a1628] border-white/10 text-white" />
                      <div className="flex-1 relative">
                        <Input 
                          type="number" 
                          placeholder="Rate" 
                          value={item.unit_price} 
                          onChange={(e) => updateItem(idx, "unit_price", e.target.value)} 
                          className={`bg-[#0a1628] border-white/10 text-white pr-10 ${item.from_estimator ? 'border-purple-500/50' : ''}`} 
                        />
                        {item.from_estimator && (
                          <button 
                            onClick={() => clearEstimatorFromItem(idx)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-300"
                            title="Clear estimator data"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <button 
                        onClick={() => openEstimatorForItem(idx)}
                        className={`px-2 py-2 rounded text-xs font-medium transition-colors ${
                          item.from_estimator 
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' 
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 border border-white/10'
                        }`}
                        title="Calculate price"
                      >
                        <Calculator size={16} />
                      </button>
                      <div className="w-20 flex items-center justify-end font-medium text-white text-sm">${(item.quantity * item.unit_price).toFixed(2)}</div>
                      <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-300"><X size={18} /></button>
                    </div>
                    {item.from_estimator && (
                      <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                        <Calculator size={12} /> Price calculated
                      </p>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} className="w-full border-dashed border-white/20 text-slate-300 hover:bg-white/5">
                  <Plus size={16} className="mr-2" /> Add Item
                </Button>
              </div>

              {/* Totals */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="text-xs text-slate-400">Discount ($)</label>
                    <Input type="number" value={formData.discount} onChange={(e) => setFormData({...formData, discount: parseFloat(e.target.value) || 0})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Tax (%)</label>
                    <Input type="number" value={formData.tax_rate} onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Paid ($)</label>
                    <Input type="number" value={formData.deposit_paid} onChange={(e) => setFormData({...formData, deposit_paid: parseFloat(e.target.value) || 0})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                </div>
                <div className="space-y-1 text-sm text-slate-300">
                  <div className="flex justify-between"><span>Subtotal</span><span>${totals.subtotal.toFixed(2)}</span></div>
                  {formData.discount > 0 && <div className="flex justify-between text-green-400"><span>Discount</span><span>-${formData.discount.toFixed(2)}</span></div>}
                  <div className="flex justify-between"><span>Tax ({formData.tax_rate}%)</span><span>${totals.taxAmount.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10 text-white"><span>Total</span><span>${totals.total.toFixed(2)}</span></div>
                  <div className="flex justify-between text-blue-400 font-bold"><span>Balance Due</span><span>${totals.balanceDue.toFixed(2)}</span></div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <h3 className="font-medium text-white mb-3">Payment Instructions</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400">Venmo</label>
                    <Input value={formData.payment_venmo} onChange={(e) => setFormData({...formData, payment_venmo: e.target.value})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400">Zelle</label>
                    <Input value={formData.payment_zelle} onChange={(e) => setFormData({...formData, payment_zelle: e.target.value})} className="mt-1 bg-[#0a1628] border-white/10 text-white" />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-[#111c2e] rounded-xl border border-white/10 p-4">
                <h3 className="font-medium text-white mb-3">Comments</h3>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={6} placeholder="Terms, conditions, thank you message..." className="bg-[#0a1628] border-white/10 text-white placeholder:text-slate-500" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fabric Estimator & Cost Calculator Modal */}
      <AnimatePresence>
        {showEstimator && (
          <div className="fixed inset-0 bg-black/70 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="min-h-screen p-4 flex items-start justify-center pt-6"
            >
              <div className="bg-[#0a1628] border border-white/10 rounded-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#111c2e] border-b border-white/10 px-4 py-3 flex items-center justify-between z-10">
                  <h3 className="text-lg font-medium text-white flex items-center gap-2">
                    <Calculator size={20} className="text-blue-400" />
                    Price Calculator
                    {estimatorItemIndex !== null && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                        Item {estimatorItemIndex + 1}
                      </span>
                    )}
                  </h3>
                  <button 
                    onClick={() => { setShowEstimator(false); setEstimatorItemIndex(null); }}
                    className="text-slate-400 hover:text-white p-1"
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className="p-4">
                  <FabricEstimator 
                    onPriceCalculated={handlePriceCalculated}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#111c2e] border-t border-white/10 z-40 shadow-lg">
        <div className="max-w-lg mx-auto flex">
          <button 
            onClick={() => setActiveTab("home")}
            className={`flex-1 py-3 flex flex-col items-center gap-1 ${activeTab === "home" ? "text-blue-400" : "text-slate-500"}`}
          >
            <Home size={20} />
            <span className="text-xs font-medium">Home</span>
          </button>
          <button 
            onClick={() => setActiveTab("clients")}
            className={`flex-1 py-3 flex flex-col items-center gap-1 ${activeTab === "clients" ? "text-blue-400" : "text-slate-500"}`}
          >
            <Users size={20} />
            <span className="text-xs font-medium">Clients</span>
          </button>
          <button 
            onClick={() => setActiveTab("invoices")}
            className={`flex-1 py-3 flex flex-col items-center gap-1 ${activeTab === "invoices" ? "text-blue-400" : "text-slate-500"}`}
          >
            <Receipt size={20} />
            <span className="text-xs font-medium">Invoices</span>
          </button>
          <button 
            onClick={() => navigate("/admin")}
            className="flex-1 py-3 flex flex-col items-center gap-1 text-slate-500"
          >
            <Menu size={20} />
            <span className="text-xs font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* Copy Invoice Dialog */}
      {showCopyDialog && invoiceToCopy && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" onClick={() => setShowCopyDialog(false)}>
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-[#111c2e] rounded-2xl w-full max-w-sm overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white text-center">Copy Invoice</h3>
              <p className="text-sm text-slate-400 text-center mt-1">
                {invoiceToCopy.invoice_number} → New Invoice
              </p>
            </div>

            {/* Options */}
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">Select what to copy:</p>
              
              {/* Copy Client Option */}
              <button
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  copyOptions.copyClient 
                    ? "bg-blue-500/20 border-blue-500" 
                    : "bg-[#0a1628] border-white/10"
                }`}
                onClick={() => setCopyOptions(prev => ({ ...prev, copyClient: !prev.copyClient }))}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  copyOptions.copyClient ? "bg-blue-500" : "bg-white/10"
                }`}>
                  {copyOptions.copyClient && <Check size={14} className="text-white" />}
                </div>
                <div className="text-left flex-1">
                  <p className="font-medium text-white">Copy Client Info</p>
                  <p className="text-xs text-slate-400">Name, email, phone, address</p>
                </div>
                {copyOptions.copyClient && invoiceToCopy.client_name && (
                  <span className="text-xs text-blue-400">{invoiceToCopy.client_name}</span>
                )}
              </button>

              {/* Copy Items Option */}
              <button
                className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  copyOptions.copyItems 
                    ? "bg-blue-500/20 border-blue-500" 
                    : "bg-[#0a1628] border-white/10"
                }`}
                onClick={() => setCopyOptions(prev => ({ ...prev, copyItems: !prev.copyItems }))}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  copyOptions.copyItems ? "bg-blue-500" : "bg-white/10"
                }`}>
                  {copyOptions.copyItems && <Check size={14} className="text-white" />}
                </div>
                <div className="text-left flex-1">
                  <p className="font-medium text-white">Copy Line Items</p>
                  <p className="text-xs text-slate-400">Products, quantities, prices</p>
                </div>
                {copyOptions.copyItems && invoiceToCopy.items?.length > 0 && (
                  <span className="text-xs text-blue-400">{invoiceToCopy.items.length} items</span>
                )}
              </button>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
                onClick={() => setShowCopyDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={executeCopyInvoice}
              >
                <Copy size={16} className="mr-2" />
                Create Copy
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Hidden Printable Invoice for Print/PDF Export */}
      {showPrintPreview && selectedInvoice && (
        <div className="fixed left-[-9999px] top-0">
          <div ref={printableInvoiceRef}>
            <PrintableInvoice invoice={selectedInvoice} />
          </div>
        </div>
      )}
    </div>
  );
};

// Printable Invoice Component - Clean format for printing/PDF
const PrintableInvoice = ({ invoice }) => {
  const calc = { 
    subtotal: invoice?.subtotal || 0, 
    taxAmount: invoice?.tax_amount || 0, 
    total: invoice?.total || 0, 
    balanceDue: invoice?.balance_due || 0 
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      color: '#1e293b', 
      padding: '24px',
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px'
    }}>
      {/* Header with Logo */}
      <div style={{ textAlign: 'center', marginBottom: '16px', paddingBottom: '16px', borderBottom: '2px solid #1e3a5f' }}>
        <img 
          src="https://customer-assets.emergentagent.com/job_laguerre-tailor/artifacts/nrobby08_handwritten%20signature%20logo%20V2%20black.png"
          alt="Laguerre"
          style={{ height: '150px', margin: '0 auto', objectFit: 'contain' }}
        />
        <p style={{ fontSize: '10px', color: '#64748b', marginTop: '-12px' }}>Custom Tailoring</p>
      </div>

      {/* Company Info & Invoice Title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ fontSize: '10px', color: '#475569' }}>
          <p>55 W Railroad Ave Building 24</p>
          <p>Garnerville, NY 10923</p>
          <p>United States</p>
          <p style={{ marginTop: '4px' }}>hello@laguerre.co</p>
          <p style={{ color: '#2563eb', fontWeight: '500' }}>www.laguerre.co</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>Invoice</h2>
        </div>
      </div>

      {/* Invoice Details Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px', fontSize: '10px' }}>
        <div>
          <div style={{ display: 'flex', marginBottom: '4px' }}>
            <span style={{ color: '#64748b', width: '80px' }}>Invoice No:</span>
            <span style={{ fontWeight: '500' }}>{invoice.invoice_number || "---"}</span>
          </div>
          <div style={{ display: 'flex', marginBottom: '4px' }}>
            <span style={{ color: '#64748b', width: '80px' }}>Date:</span>
            <span>{format(new Date(invoice.created_at || new Date()), "MM/dd/yyyy")}</span>
          </div>
          <div style={{ display: 'flex', marginBottom: '4px' }}>
            <span style={{ color: '#64748b', width: '80px' }}>Terms:</span>
            <span>NET 0</span>
          </div>
          <div style={{ display: 'flex' }}>
            <span style={{ color: '#64748b', width: '80px' }}>Due Date:</span>
            <span>{invoice.due_date ? format(new Date(invoice.due_date), "MM/dd/yyyy") : "---"}</span>
          </div>
        </div>
        <div>
          <p style={{ color: '#64748b', marginBottom: '4px' }}>Bill To:</p>
          <p style={{ fontWeight: '500' }}>{invoice.client_name || "Client Name"}</p>
          {invoice.client_phone && <p>{invoice.client_phone}</p>}
          {invoice.client_email && <p style={{ color: '#64748b' }}>{invoice.client_email}</p>}
        </div>
      </div>

      {/* Suit Arrival Badge */}
      {invoice.estimated_arrival && (
        <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '8px', marginBottom: '16px', textAlign: 'center' }}>
          <span style={{ fontSize: '10px', color: '#2563eb', fontWeight: '500' }}>
            📦 Estimated Suit Arrival: {format(new Date(invoice.estimated_arrival), "MMM dd, yyyy")}
          </span>
        </div>
      )}

      {/* Items Table */}
      <table style={{ width: '100%', marginBottom: '16px', fontSize: '10px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ textAlign: 'left', padding: '8px 0', fontWeight: '500', color: '#475569' }}>Description</th>
            <th style={{ textAlign: 'center', padding: '8px 0', fontWeight: '500', color: '#475569', width: '60px' }}>Qty</th>
            <th style={{ textAlign: 'right', padding: '8px 0', fontWeight: '500', color: '#475569', width: '70px' }}>Rate</th>
            <th style={{ textAlign: 'right', padding: '8px 0', fontWeight: '500', color: '#475569', width: '70px' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(invoice.items || []).filter(item => item.description).map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={{ padding: '8px 0', whiteSpace: 'pre-line' }}>{item.description}</td>
              <td style={{ padding: '8px 0', textAlign: 'center' }}>{item.quantity}</td>
              <td style={{ padding: '8px 0', textAlign: 'right' }}>${item.unit_price.toFixed(2)}</td>
              <td style={{ padding: '8px 0', textAlign: 'right' }}>${(item.quantity * item.unit_price).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div style={{ width: '180px', fontSize: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Subtotal</span>
            <span>${calc.subtotal.toFixed(2)}</span>
          </div>
          {invoice.discount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#16a34a' }}>
              <span>Discount</span>
              <span>-${invoice.discount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>TAX {invoice.tax_rate}%</span>
            <span>${calc.taxAmount.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid #e2e8f0', paddingTop: '4px' }}>
            <span>Total</span>
            <span>${calc.total.toFixed(2)}</span>
          </div>
          {invoice.deposit_paid > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', color: '#64748b' }}>
              <span>Paid</span>
              <span>${invoice.deposit_paid.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Balance Due Box */}
      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', padding: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', color: '#1e3a5f' }}>Balance Due</span>
        <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e3a5f' }}>${calc.balanceDue.toFixed(2)}</span>
      </div>

      {/* Payment Instructions */}
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontWeight: '500', color: '#334155', marginBottom: '8px' }}>Payment Instructions</p>
        <div style={{ fontSize: '10px', color: '#475569' }}>
          <p>Venmo: {invoice.payment_venmo || "@Richardlaguerre"}</p>
          <p>Zelle: {invoice.payment_zelle || "9146194784"}</p>
        </div>
      </div>

      {/* Comments */}
      {invoice.notes && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
          <p style={{ fontWeight: '500', color: '#334155', marginBottom: '8px' }}>Comments</p>
          <p style={{ fontSize: '10px', color: '#475569', whiteSpace: 'pre-line' }}>{invoice.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: '9px', color: '#94a3b8' }}>
        <p>Thank you for your business!</p>
        <p>Laguerre Custom Tailoring • hello@laguerre.co • www.laguerre.co</p>
      </div>
    </div>
  );
};

export default AdminInvoicePage;
